import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { axios } from "@/client/libs/axios";

/**
 * Crédits IA — solde, historique et packs de recharge.
 *
 * - Le solde est la source de vérité « Point IA » (GET /ai/wallet) ;
 * - les packs proviennent du catalogue Rate (GET /rates/ai-packs),
 *   configurables par la finance sans déploiement ;
 * - l'historique vient du ledger (GET /ai/wallet/history).
 */

const walletSchema = z.object({
  balance: z.number(),
  totalPurchased: z.number(),
  totalGranted: z.number(),
  totalConsumed: z.number(),
});

const walletTransactionSchema = z.object({
  id: z.string(),
  type: z.enum(["TOPUP", "GRANT", "CONSUME", "REFUND", "ADJUST", "EXPIRE"]),
  points: z.number(),
  balanceBefore: z.number(),
  balanceAfter: z.number(),
  reference: z.string().nullable().optional(),
  feature: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
  createdAt: z.string(),
});

const aiPackSchema = z.object({
  id: z.string(),
  name: z.string(),
  price: z.number(),
  aiPoints: z.number(),
  validity: z.number(),
  description: z.string().nullable().optional(),
  isRecommended: z.boolean().optional(),
  discountFcfa: z.number().optional(),
  ambassadorCommissionFcfa: z.number().optional(),
});

export type AiWallet = z.infer<typeof walletSchema>;
export type AiWalletTransaction = z.infer<typeof walletTransactionSchema>;
export type AiPack = z.infer<typeof aiPackSchema>;

export const AI_WALLET_QUERY_KEY = ["ai-wallet"] as const;
export const AI_WALLET_HISTORY_QUERY_KEY = ["ai-wallet", "history"] as const;
export const AI_PACKS_QUERY_KEY = ["ai-packs"] as const;

export const useAiWallet = () => {
  return useQuery({
    queryKey: AI_WALLET_QUERY_KEY,
    queryFn: async () => {
      const response = await axios.get<unknown>("/ai/wallet");
      return walletSchema.parse(response.data);
    },
    staleTime: 5_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
};

export const useAiWalletHistory = () => {
  return useQuery({
    queryKey: AI_WALLET_HISTORY_QUERY_KEY,
    queryFn: async () => {
      const response = await axios.get<unknown[]>("/ai/wallet/history");
      return z.array(walletTransactionSchema).parse(response.data);
    },
  });
};

export const useAiPacks = (enabled = true) => {
  return useQuery({
    queryKey: AI_PACKS_QUERY_KEY,
    queryFn: async () => {
      const response = await axios.get<unknown>("/rates/ai-packs");
      return z.array(aiPackSchema).parse(response.data);
    },
    enabled,
  });
};
